import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  TextField,
  Paper,
  Tabs,
  Tab,
  IconButton,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  InputAdornment,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SearchIcon from "@mui/icons-material/Search";

import Sidebar from './Sidebar';

// ─── Helper: safely parse a JSON string, returning fallback on error ──────────
const safeParseJSON = (str, fallback) => {
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && parsed !== undefined ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const SkillsSection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [skills, setSkills] = useState([]);
  const [textSkill, setTextSkill] = useState("");
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [aiPanelExpanded, setAiPanelExpanded] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ─── Initialize template & theme from location.state first,
  //     then fall back to localStorage so back-navigation never loses the choice ─
  const getInitialTemplate = () => {
    if (location.state?.selectedTemplate) return location.state.selectedTemplate;
    return safeParseJSON(localStorage.getItem("selectedTemplate"), 1);
  };

  const getInitialTheme = () => {
    if (location.state?.selectedTheme) return location.state.selectedTheme;
    return safeParseJSON(localStorage.getItem("selectedTheme"), "#1a2b47");
  };

  const [selectedTemplate, setSelectedTemplate] = useState(getInitialTemplate);
  const [selectedTheme, setSelectedTheme] = useState(getInitialTheme);

  // ─── Persist template & theme to localStorage whenever they change ────────
  useEffect(() => {
    localStorage.setItem("selectedTemplate", JSON.stringify(selectedTemplate));
  }, [selectedTemplate]);

  useEffect(() => {
    localStorage.setItem("selectedTheme", JSON.stringify(selectedTheme));
  }, [selectedTheme]);

  // ─── Sync from location.state when navigation supplies fresh values ────────
  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: tmpl, selectedTheme: thm } = location.state;
      if (tmpl) {
        setSelectedTemplate(tmpl);
        localStorage.setItem("selectedTemplate", JSON.stringify(tmpl));
      }
      if (thm) {
        setSelectedTheme(thm);
        localStorage.setItem("selectedTheme", JSON.stringify(thm));
      }
    }
  }, [location.state]);

  // Fetch AI recommendations
  const fetchAISkillRecommendations = async (jobTitle) => {
    if (!jobTitle.trim()) return;

    setAiLoading(true);
    try {
      const response = await fetch('https://resume-maker-lc.onrender.com/api/ai/skills', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobTitle }),
      });

      if (!response.ok) throw new Error('API failed');

      const data = await response.json();
      setAiRecommendations(data.skills || []);

    } catch (error) {
      console.error("AI API Error:", error);
      setAiRecommendations([]);
    } finally {
      setAiLoading(false);
    }
  };

  // Debounced search for AI
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (search.length > 2) {
        fetchAISkillRecommendations(search);
      } else {
        setAiRecommendations([]);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  // Load from localStorage
  useEffect(() => {
    const savedSkills = localStorage.getItem("skills");
    if (savedSkills) setSkills(JSON.parse(savedSkills));
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem("skills", JSON.stringify(skills));
  }, [skills]);

  const handleAddSkill = (skill) => {
    if (skill.trim() !== "" && !skills.find((s) => s.name === skill)) {
      const newSkills = [...skills, { name: skill, rating: 0 }];
      setSkills(newSkills);
      setTextSkill("");
    }
  };

  const handleDelete = (index) => {
    const newSkills = skills.filter((_, i) => i !== index);
    setSkills(newSkills);
  };

  const handleRatingChange = (index, newRating) => {
    const updated = [...skills];
    updated[index].rating = newRating;
    setSkills(updated);
  };

  const applyFormat = (startTag, endTag) => {
    const textarea = document.getElementById("skill-box");
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textSkill.substring(start, end);
    const newText =
      textSkill.substring(0, start) +
      startTag +
      selectedText +
      endTag +
      textSkill.substring(end);

    setHistory([...history, textSkill]);
    setTextSkill(newText);
  };

  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setRedoStack([textSkill, ...redoStack]);
    setHistory(history.slice(0, -1));
    setTextSkill(prev);
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[0];
    setHistory([...history, textSkill]);
    setRedoStack(redoStack.slice(1));
    setTextSkill(next);
  };

  const saveSkills = () => {
    navigate('/ResumeSummary');
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  // ─── Go Back — always pass the persisted template & theme ─────────────────
  const handleGoBack = () => {
    navigate('/skills', {
      state: { selectedTemplate, selectedTheme },
    });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Desktop Sidebar */}
      {!isMobile && <Sidebar />}

      {/* Mobile Sidebar Drawer */}
      {isMobile && (
        <>
          <IconButton
            onClick={toggleMobileSidebar}
            sx={{
              position: "fixed",
              top: 16,
              left: 16,
              zIndex: 1300,
              backgroundColor: "white",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Drawer
            variant="temporary"
            open={mobileSidebarOpen}
            onClose={toggleMobileSidebar}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 280,
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <Sidebar onMobileClose={toggleMobileSidebar} />
          </Drawer>
        </>
      )}

      {/* AI Side Panel - Desktop */}
      {!isMobile && (
        <Box sx={{
          width: 320,
          bgcolor: '#EFF7FF',
          p: 2,
          borderRight: '1px solid #B3D9F7',
          overflowY: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 280,
          top: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AutoAwesomeIcon sx={{ mr: 1, color: '#002B5B' }} />
            <Typography variant="h6" fontWeight="bold" color="#002B5B">
              AI Skills Assistant
            </Typography>
          </Box>

          {/* AI Search */}
          <Card sx={{ mb: 2, border: "1px solid #B3D9F7" }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: "#002B5B" }}>
                🔍 AI Skill Search
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter job title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && search.trim().length > 2) {
                    fetchAISkillRecommendations(search.trim());
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {aiLoading ? (
                        <CircularProgress size={16} sx={{ color: "#0288D1" }} />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => search.trim().length > 2 && fetchAISkillRecommendations(search.trim())}
                          sx={{ color: "#0288D1" }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#B3D9F7" },
                    "&:hover fieldset": { borderColor: "#0288D1" },
                    "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: "#0288D1" }}>
                AI will suggest relevant skills
              </Typography>
            </CardContent>
          </Card>

          {/* AI Loading */}
          {aiLoading && (
            <Card sx={{ mb: 2, textAlign: 'center', p: 2, border: "1px solid #B3D9F7" }}>
              <CircularProgress size={20} sx={{ mr: 1, color: "#0288D1" }} />
              <Typography variant="body2">AI is analyzing job market...</Typography>
            </Card>
          )}

          {/* AI Recommendations */}
          {aiRecommendations.length > 0 && (
            <Card sx={{ mb: 2, border: "1px solid #B3D9F7" }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AutoAwesomeIcon sx={{ mr: 1, color: '#0288D1' }} />
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#002B5B" }}>
                    AI Suggested Skills
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#0288D1" }} gutterBottom>
                  For: "{search}"
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {aiRecommendations.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      onClick={() => handleAddSkill(skill)}
                      clickable
                      variant="outlined"
                      icon={<AddIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        borderColor: "#002B5B",
                        color: "#002B5B",
                        "& .MuiChip-icon": { color: "#002B5B" },
                        "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.08)" },
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Quick Add */}
          <Card sx={{ border: "1px solid #B3D9F7" }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: "#002B5B" }}>
                ⚡ Quick Add Skills
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {["Communication", "Teamwork", "Problem Solving", "Python", "JavaScript", "Project Management"].map(skill => (
                  <Chip
                    key={skill}
                    label={skill}
                    size="small"
                    onClick={() => handleAddSkill(skill)}
                    clickable
                    variant="outlined"
                    icon={<AddIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      borderColor: "#B3D9F7",
                      color: "#002B5B",
                      "& .MuiChip-icon": { color: "#0288D1" },
                      "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.08)", borderColor: "#0288D1" },
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          py: isMobile ? 4 : 6,
          ml: isMobile ? 0 : '640px',
          px: isMobile ? 2 : 3,
          pt: isMobile ? 8 : 6
        }}
      >
        {/* Go Back Button */}
        <Typography
          variant="body2"
          sx={{
            color: "#002B5B",
            fontWeight: "bold",
            mb: 2,
            cursor: "pointer",
            "&:hover": {
              color: "#0288D1",
              textDecoration: "underline",
            },
            transition: "color 0.3s ease",
            fontSize: isSmallMobile ? "0.875rem" : "inherit",
          }}
          onClick={handleGoBack}
        >
          ← Go Back
        </Typography>

        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight="bold"
          gutterBottom
          sx={{ fontSize: isSmallMobile ? "1.25rem" : "inherit" }}
        >
          What skills would you like to highlight?
        </Typography>
        <Typography
          variant="body1"
          gutterBottom
          sx={{
            fontSize: isSmallMobile ? "0.875rem" : "inherit",
            lineHeight: 1.6,
            color: "#0288D1",
          }}
        >
          Choose from our pre-written examples below or write your own.
          {!isMobile && " AI suggestions appear on the left."}
        </Typography>

        {/* Mobile AI Assistant Section */}
        {isMobile && (
          <Box sx={{ mb: 3 }}>
            <Accordion
              expanded={aiPanelExpanded}
              onChange={() => setAiPanelExpanded(!aiPanelExpanded)}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: '8px !important',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#EFF7FF',
                  borderRadius: aiPanelExpanded ? '8px 8px 0 0' : '8px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SmartToyIcon sx={{ mr: 1, color: '#002B5B' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="#002B5B">
                    AI Skills Assistant
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                {/* AI Search */}
                <Card sx={{ mb: 2, border: "1px solid #B3D9F7" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: "#002B5B" }}>
                      🔍 AI Skill Search
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter job title..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && search.trim().length > 2) {
                          fetchAISkillRecommendations(search.trim());
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {aiLoading ? (
                              <CircularProgress size={16} sx={{ color: "#0288D1" }} />
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => search.trim().length > 2 && fetchAISkillRecommendations(search.trim())}
                                sx={{ color: "#0288D1" }}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#B3D9F7" },
                          "&:hover fieldset": { borderColor: "#0288D1" },
                          "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "#0288D1" }}>
                      AI will suggest relevant skills
                    </Typography>
                  </CardContent>
                </Card>

                {/* AI Loading */}
                {aiLoading && (
                  <Card sx={{ mb: 2, textAlign: 'center', p: 2, border: "1px solid #B3D9F7" }}>
                    <CircularProgress size={20} sx={{ mr: 1, color: "#0288D1" }} />
                    <Typography variant="body2">AI is analyzing job market...</Typography>
                  </Card>
                )}

                {/* AI Recommendations */}
                {aiRecommendations.length > 0 && (
                  <Card sx={{ mb: 2, border: "1px solid #B3D9F7" }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AutoAwesomeIcon sx={{ mr: 1, color: '#0288D1' }} />
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#002B5B" }}>
                          AI Suggested Skills
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: "#0288D1" }} gutterBottom>
                        For: "{search}"
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {aiRecommendations.map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            size="small"
                            onClick={() => handleAddSkill(skill)}
                            clickable
                            variant="outlined"
                            icon={<AddIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              borderColor: "#002B5B",
                              color: "#002B5B",
                              "& .MuiChip-icon": { color: "#002B5B" },
                              "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.08)" },
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Quick Add */}
                <Card sx={{ border: "1px solid #B3D9F7" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: "#002B5B" }}>
                      ⚡ Quick Add Skills
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {["Communication", "Teamwork", "Problem Solving", "Python", "JavaScript", "Project Management"].map(skill => (
                        <Chip
                          key={skill}
                          label={skill}
                          size="small"
                          onClick={() => handleAddSkill(skill)}
                          clickable
                          variant="outlined"
                          icon={<AddIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            borderColor: "#B3D9F7",
                            color: "#002B5B",
                            "& .MuiChip-icon": { color: "#0288D1" },
                            "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.08)", borderColor: "#0288D1" },
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        <Grid container spacing={3} mt={isMobile ? 1 : 2}>
          <Grid item xs={12} sx={{ width: isMobile ? "100%" : "700px" }}>
            <Paper sx={{ p: isMobile ? 2 : 5, width: "100%", border: "1px solid #B3D9F7" }}>
              <Tabs
                value={tab}
                onChange={(e, val) => setTab(val)}
                variant={isMobile ? "fullWidth" : "standard"}
                sx={{
                  "& .MuiTab-root.Mui-selected": { color: "#002B5B" },
                  "& .MuiTabs-indicator": { backgroundColor: "#002B5B" },
                }}
              >
                <Tab label="Add Skills" />
                <Tab label="Skills Rating" />
              </Tabs>

              {tab === 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <TextField
                      id="skill-box"
                      fullWidth
                      size="small"
                      placeholder="Type a skill and press Enter or click +"
                      value={textSkill}
                      onChange={(e) => setTextSkill(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddSkill(textSkill);
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => handleAddSkill(textSkill)}
                              sx={{ color: "#0288D1" }}
                            >
                              <AddIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#B3D9F7" },
                          "&:hover fieldset": { borderColor: "#0288D1" },
                          "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                        },
                      }}
                    />
                  </Box>

                  {/* Added Skills */}
                  <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {skills.map((skill, idx) => (
                      <Chip
                        key={idx}
                        label={skill.name}
                        onDelete={() => handleDelete(idx)}
                        deleteIcon={<DeleteIcon />}
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          borderColor: "#002B5B",
                          color: "#002B5B",
                          "& .MuiChip-deleteIcon": { color: "#0288D1" },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {tab === 1 && (
                <Box sx={{ mt: 2 }}>
                  {skills.map((skill, idx) => (
                    <Box key={idx} sx={{ mb: 3 }}>
                      <Typography sx={{ mb: 1, fontSize: isSmallMobile ? "0.875rem" : "inherit" }}>
                        {skill.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={skill.rating}
                          onChange={(e) => handleRatingChange(idx, Number(e.target.value))}
                          style={{ flex: 1, accentColor: "#002B5B" }}
                        />
                        <Typography sx={{ minWidth: 40, fontSize: isSmallMobile ? "0.875rem" : "inherit" }}>
                          {skill.rating}%
                        </Typography>
                        <IconButton color="error" onClick={() => handleDelete(idx)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={skill.rating}
                        sx={{
                          height: 8,
                          borderRadius: 5,
                          mt: 1,
                          backgroundColor: "#EFF7FF",
                          "& .MuiLinearProgress-bar": { backgroundColor: "#0288D1" },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <Typography variant="body2" sx={{ mt: 2, fontSize: isSmallMobile ? "0.875rem" : "inherit" }}>
                Skills: {skills.length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Bottom Buttons */}
        <Box sx={{
          display: "flex",
          gap: 2,
          mt: 3,
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "flex-start"
        }}>
          <Button
            variant="contained"
            sx={{
              px: 4,
              borderRadius: 3,
              fontWeight: "bold",
              backgroundColor: "#002B5B",
              flex: isMobile ? 1 : "none",
              "&:hover": { backgroundColor: "#0288D1" },
            }}
            onClick={() => {
              saveSkills();
              navigate("/ResumeSummary", {
                state: { selectedTemplate, selectedTheme },
              });
            }}
          >
            Next: Summary
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SkillsSection;